import {createSlice} from '@reduxjs/toolkit';
import * as Immutable from 'immutable';

import type {PayloadAction} from '@reduxjs/toolkit/src/createAction';
import type {CaseReducer} from '@reduxjs/toolkit/src/createReducer';
import type {LocaleModule} from 'constants/intl-constants';
import type {Set} from 'immutable';
import type {ResolvedIntlConfig} from 'react-intl/src/types';

export type IntlState = {
    formats: Readonly<ResolvedIntlConfig['formats']>;
    messages: Readonly<Record<string, string>>;
    modules: Set<LocaleModule>;
};

type IntlSliceCaseReducers = {
    setFormats: CaseReducer<IntlState, PayloadAction<IntlState['formats']>>;
    setMessages: CaseReducer<
        IntlState,
        PayloadAction<{
            moduleNames: LocaleModule[];
            messages: IntlState['messages'];
        }>
    >;
};

export const formats: IntlState['formats'] = {
    /**
     * Warning: do not specify any `timeZone` setting in these formats. Most
     * browsers do not support any value for `timeZone` other than 'UTC', so
     * we need to fall back to the local time to avoid throwing exceptions.
     */
    date: {
        longDateTime: {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        },
        longDateTimeWithTimezone: {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZoneName: 'short',
        },
        preciseDateTime: {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
        },
        shortDateTime: {
            // Sep 20, 2019, 3:12 PM
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        },
        mediumDate: {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        },
        longDate: {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        },
        shortDate: {
            // en-US: 09/20/2019
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        },
        date: {
            day: 'numeric',
        },
        month: {
            month: 'long',
        },
        monthAndDay: {
            month: 'short',
            day: 'numeric',
        },
        yearAndMonth: {
            year: 'numeric',
            month: 'short',
        },
        timeOfDay: {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        },
    },
};

export const initialState = {
    formats,
    messages: {},
    modules: Immutable.Set<LocaleModule>(),
} satisfies IntlState as Readonly<IntlState>;

const intlSlice = createSlice<IntlState, IntlSliceCaseReducers, 'intl'>({
    name: 'intl',
    initialState,
    reducers: {
        setFormats: (state, action) => {
            state.formats = {...state.formats, ...action.payload};
        },
        setMessages: (state, action) => {
            const {moduleNames, messages} = action.payload;

            state.modules = state.modules.concat(moduleNames);
            state.messages = {...state.messages, ...messages};
        },
    },
});

export const {setFormats, setMessages} = intlSlice.actions;

export default intlSlice.reducer;
